/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Formats a time string into 12-hour format with AM/PM only (e.g. "4:03 PM").
 * Does not perform any timezone conversion or offset adjustment.
 */
export const formatTime12Hour = (timeStr?: string | null): string => {
  if (!timeStr) return "";
  const trimmed = timeStr.trim();
  // If already formatted with AM/PM (e.g. "4:03 PM", "04:03 PM")
  const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i);
  if (ampmMatch) {
    const rawHour = parseInt(ampmMatch[1], 10);
    const minute = ampmMatch[2];
    const ampm = ampmMatch[3].toUpperCase();
    const hour12 = rawHour === 0 ? 12 : rawHour > 12 ? rawHour % 12 : rawHour;
    return `${hour12}:${minute} ${ampm}`;
  }

  // If 24-hour time like "16:03" or "16:03:00"
  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (match24) {
    const rawHour = parseInt(match24[1], 10);
    const minute = match24[2];
    const ampm = rawHour >= 12 ? "PM" : "AM";
    const hour12 = rawHour % 12 === 0 ? 12 : rawHour % 12;
    return `${hour12}:${minute} ${ampm}`;
  }

  return trimmed;
};

/**
 * Formats an event date and time string directly from the startsAt value (or separate date and time).
 *
 * Requirements:
 * 1. Display the event date and time directly from the startsAt value provided by the API.
 * 2. Convert the time to 12-hour format with AM/PM only. Do not convert the time to AST, UTC, or the user's local timezone.
 * 3. Do not apply any timezone offset or change the original date or time.
 *
 * Example:
 * API response: startsAt: "2026-11-26T16:03:00.000Z"
 * Expected display: "Nov 26, 2026, 4:03 PM"
 */
export const formatEventDateTime = (
  dateStr?: string | null,
  timeStr?: string | null
): string => {
  if (!dateStr && !timeStr) return "";
  if (!dateStr) return formatTime12Hour(timeStr);

  const trimmed = dateStr.trim();

  // If already formatted like "Nov 26, 2026, 4:03 PM", return as is
  if (
    /^[A-Za-z]{3}\s+\d{1,2},\s*\d{4},\s*\d{1,2}:\d{2}\s+(?:AM|PM)$/i.test(
      trimmed
    )
  ) {
    return trimmed;
  }

  // Match ISO / standard date patterns:
  // e.g. "2026-11-26T16:03:00.000Z", "2026-11-26 16:03:00", "2026-11-26T16:03", "2026-11-26"
  const isoMatch = trimmed.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/
  );

  if (isoMatch) {
    const year = isoMatch[1];
    const monthNum = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    const month = MONTH_NAMES[monthNum - 1] || isoMatch[2];

    const formattedDate = `${month} ${day}, ${year}`;

    // If time is embedded in dateStr (e.g. startsAt: "2026-11-26T16:03:00.000Z")
    if (isoMatch[4] !== undefined && isoMatch[5] !== undefined) {
      const rawHour = parseInt(isoMatch[4], 10);
      const minute = isoMatch[5];
      const ampm = rawHour >= 12 ? "PM" : "AM";
      const hour12 = rawHour % 12 === 0 ? 12 : rawHour % 12;
      return `${formattedDate}, ${hour12}:${minute} ${ampm}`;
    }

    // Otherwise if timeStr is provided separately
    if (timeStr) {
      const formattedTime = formatTime12Hour(timeStr);
      if (formattedTime) {
        return `${formattedDate}, ${formattedTime}`;
      }
    }

    return formattedDate;
  }

  // Fallback: If it's already formatted or non-standard
  try {
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      const month = MONTH_NAMES[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      const rawHour = date.getUTCHours();
      const minute = String(date.getUTCMinutes()).padStart(2, "0");
      const ampm = rawHour >= 12 ? "PM" : "AM";
      const hour12 = rawHour % 12 === 0 ? 12 : rawHour % 12;
      return `${month} ${day}, ${year}, ${hour12}:${minute} ${ampm}`;
    }
  } catch {
    // ignore
  }

  return trimmed;
};
